import React, { useState, useEffect } from 'react';
import { ApiService } from 'app/servicesTwo/ApiService';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Grid,
    InputAdornment,
    IconButton,
    CircularProgress,
    Stack,
    Alert,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/Upload';
import BadgeCard from 'app/components/BadgeCard/BadgeCard';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import InputMask from 'react-input-mask';

const BadgeUpdatePublic = () => {
    const [cpf, setCpf] = useState('');
    const [loading, setLoading] = useState(false);
    const [volunteer, setVolunteer] = useState(null);
    const [error, setError] = useState('');
    const [nomeCracha, setNomeCracha] = useState('');
    const [file, setFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [badgeLayout, setBadgeLayout] = useState('vertical');
    const Swal = useSwalWrapper();

    const handleSearch = async () => {
        if (!cpf) return;
        setLoading(true);
        setError('');
        setVolunteer(null);

        try {
            const response = await ApiService.get(`/public/volu-badge`, { params: { cpf } });
            const data = response.data;
            setVolunteer(data);
            setNomeCracha(data.nome_cracha || '');
            
            if (data.foto_cracha) {
                setPreviewImage(`${process.env.REACT_APP_API_KEY}/images/${data.foto_cracha}`);
            } else if (data.foto_perfil_original) {
                setPreviewImage(`${process.env.REACT_APP_API_KEY}/images/${data.foto_perfil_original}`);
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Voluntário não encontrado ou erro ao buscar.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewImage(URL.createObjectURL(selectedFile));
        }
    };

    const handleSave = async () => {
        if (!volunteer) return;
        setLoading(true);

        const formData = new FormData();
        formData.append('nome_cracha', nomeCracha);
        if (file) {
            formData.append('foto_cracha', file);
        }

        try {
            await ApiService.put(`/public/volu-badge/${volunteer.voluntario_id}`, formData);
            Swal.fire({
                icon: 'success',
                title: 'Sucesso!',
                text: 'Dados do crachá atualizados com sucesso.',
            })
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: 'Erro ao salvar os dados. Tente novamente.',
            });
        } finally {
            setLoading(false);
        }
    };

    const badgeUser = volunteer ? {
        User: {
            nome: volunteer.nome_completo,
            genero: volunteer.genero,
        },
        nome_cracha: nomeCracha,
        foto_cracha: file || volunteer.foto_cracha || volunteer.foto_perfil_original,
        codigo_antigo_voluntariado: volunteer.codigo_antigo_voluntariado,
        codigo_voluntario_feesp: volunteer.codigo_voluntario_feesp
    } : null;

    if (badgeUser) {
        badgeUser.foto_cracha = file ? file : (volunteer.foto_cracha || null);
        if (!badgeUser.foto_cracha) {
             badgeUser.User.imagem_perfil = volunteer.foto_perfil_original;
        }
    }


    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
             <Paper sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    Atualização de Crachá
                </Typography>
                <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4 }}>
                    Digite seu CPF para buscar seus dados e atualizar a foto ou nome do seu crachá.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                     <InputMask
                        mask="999.999.999-99"
                        value={cpf}
                        onChange={(e) => setCpf(e.target.value)}
                    >
                        {() => (
                            <TextField
                                label="CPF"
                                variant="outlined"
                                sx={{ mr: 2 }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                            />
                        )}
                    </InputMask>
                    <Button 
                        color="info"
                        variant="contained" 
                        size="large" 
                        onClick={handleSearch}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit"/> : <SearchIcon />}
                    >
                        Buscar
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {volunteer && (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="h6" gutterBottom>
                                Pré-visualização
                            </Typography>
                            <ToggleButtonGroup
                                value={badgeLayout}
                                exclusive
                                size="small"
                                onChange={(e, value) => value && setBadgeLayout(value)}
                                sx={{ mb: 2 }}
                            >
                                <ToggleButton value="vertical">Vertical</ToggleButton>
                                <ToggleButton value="horizontal">Horizontal</ToggleButton>
                            </ToggleButtonGroup>
                             <div style={{ transform: 'scale(0.8)', transformOrigin: 'top center' }}>
                                <BadgeCard user={badgeUser} layout={badgeLayout} />
                             </div>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>
                                Editar Informações
                            </Typography>
                            
                            <TextField
                                fullWidth
                                label="Nome no Crachá"
                                value={nomeCracha}
                                onChange={(e) => setNomeCracha(e.target.value)}
                                inputProps={{ maxLength: 32 }}
                                helperText="Como você quer ser chamado no crachá (máx. 32 caracteres)"
                                margin="normal"
                            />

                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    Foto do Crachá:
                                </Typography>
                                <Button
                                    color="info"
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    fullWidth
                                >
                                    Carregar Nova Foto
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />
                                </Button>
                                {file && (
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                        Arquivo selecionado: {file.name}
                                    </Typography>
                                )}
                            </Box>

                             <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                size="large"
                                onClick={handleSave}
                                disabled={loading}
                                sx={{ mt: 2 }}
                            >
                                {loading ? 'Salvando...' : 'Salvar Alterações'}
                            </Button>

                        </Grid>
                    </Grid>
                )}
             </Paper>
        </Container>
    );
};

export default BadgeUpdatePublic;
